import { Button, Flex, IconAddLine, ToggleGroup } from "@instructure/ui";
import { t } from "i18next";
import React, { createContext, useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { LearningOutcomesService } from "../../../services/LearningOutcomesService";
import { OutcomeCategoryService } from "../../../services/OutcomeCategoryService";
import { sort } from "../../../utils/utils";
import { Card } from "../../Card";
import { useNotification } from "../../CustomHooks/NotificationProvider";
import EditOutcomeModal from "../../Outcomes/Review/EditOutcomeModal";
import ArchiveToggle from "../ArchiveToggle";
import CategoriesModal from "../CategoriesModal/CategoriesModal";
import { isArchived, sortOutcomes } from "../utils";
import PLOCard from "./PLOCard";
import PLOCategories from "./PLOCategories";
import PLOUploadModal from "./PLOUploadModal";

const CategoriesContext = createContext();
const UncategorizedContext = createContext();

const CategoryList = ({ program }) => {
  const showNotification = useNotification();
  const [loadingPlos, setLoadingPlos] = useState(true);

  const [categories, setCategories] = useState([]);
  const filterCategories = (categories) => {
    const unarchivedCategories = [];
    categories.forEach((category) => {
      const unarchivedLos = category.outcomes.filter((lo) => !isArchived(lo));

      if (category.outcomes.length === 0 || unarchivedLos.length > 0) {
        unarchivedCategories.push({ ...category, outcomes: unarchivedLos });
      }
    });
    return unarchivedCategories;
  };
  const unarchivedCategories = filterCategories(categories);

  const [uncategorizedLos, setUncategorizedLos] = useState([]);
  const unarchivedUncategorizedLos = uncategorizedLos.filter(
    (lo) => !isArchived(lo)
  );

  useEffect(() => {
    if (loadingPlos) {
      OutcomeCategoryService.getOutcomeCategoriesByContext(program.id)
        .then((categories) => {
          categories = categories.filter((c) => c.outcome_type === "PSLO");
          categories.forEach((cat) => {
            sortOutcomes(cat.outcomes);
            cat.outcomes.forEach((o) => {
              o.category_id = cat.id;
            });
          });
          categories = sort(categories, (cat) => cat.name);
          setCategories(categories);
          OutcomeCategoryService.getUncategorizedOutcomesByContext(
            program.id
          ).then((uncategorized) => {
            let outcomes;
            if (program.label === "hierarchy level") {
              outcomes = uncategorized.level_outcomes;
            } else {
              outcomes = uncategorized.program_outcomes;
            }
            outcomes.push(...(uncategorized.unapproved_outcomes ?? []));
            setUncategorizedLos(sortOutcomes(outcomes));
            setLoadingPlos(false);
          });
        })
        .catch(() => setLoadingPlos(false));
    }
  }, [program, loadingPlos]);

  const handleSaveOutcome = async (newName, newStatement, outcomeVariety) => {
    try {
      const newOutcome = await LearningOutcomesService.createLearningOutcome({
        name: newName,
        seq: uncategorizedLos.length + 1,
        statement: newStatement,
        outcomeType: "PSLO",
        programId: program.id,
        outcomeVariety: outcomeVariety,
      });
      setUncategorizedLos((prevLos) => sortOutcomes([...prevLos, newOutcome]));
      showNotification(
        t("notifications.general.createSuccess", {
          obj: t("plo.programLearningOutcome"),
        })
      );
    } catch {
      throw new Error(
        t("notifications.general.createError", {
          obj: t("plo.programLearningOutcome"),
        })
      );
    }
  };

  const handleArchiveOutcome = (outcome, term) => {
    setUncategorizedLos((prevLos) =>
      prevLos.map((lo) => {
        if (lo.id === outcome.id) {
          lo.archive_term = term;
        }
        return lo;
      })
    );
    setCategories((prevCategories) =>
      prevCategories.map((cat) => {
        if (cat.outcomes && cat.outcomes.length > 0) {
          cat.outcomes = cat.outcomes.map((lo) => {
            if (lo.id === outcome.id) {
              lo.archive_term = term;
            }
            return lo;
          });
        }
        return cat;
      })
    );
  };

  const handleUpdateRatingScale = (newLo) => {
    setUncategorizedLos((prevLos) =>
      prevLos.map((lo) => {
        if (lo.id === newLo.id) {
          lo.rating_scales = newLo.rating_scales;
        }
        return lo;
      })
    );
    setCategories((prevCategories) =>
      prevCategories.map((cat) => {
        if (cat.outcomes && cat.outcomes.length > 0) {
          cat.outcomes = cat.outcomes.map((lo) => {
            if (lo.id === newLo.id) {
              lo.rating_scales = newLo.rating_scales;
            }
            return lo;
          });
        }
        return cat;
      })
    );
  };

  return loadingPlos ? (
    <Skeleton
      count={3}
      style={{ height: 106, borderRadius: 5, marginBottom: 10 }}
    />
  ) : (
    <CategoriesContext.Provider value={{ categories, setCategories }}>
      <UncategorizedContext.Provider
        value={{ uncategorizedLos, setUncategorizedLos }}
      >
        <Flex justifyItems="end" gap="small" wrap="wrap" margin="0 0 small 0">
          <Flex.Item>
            <AddOutcomeButton
              outcome={{
                name: "",
                statement: "",
                sequence: uncategorizedLos.length + 1,
                outcome_variety: "outcome",
              }}
              handleSave={handleSaveOutcome}
            />
          </Flex.Item>
          <Flex.Item>
            <CategoriesModal
              data={[...uncategorizedLos]}
              setLoader={setLoadingPlos}
              title={t("categories.addPloCategory")}
              loType="PSLO"
              service={(category) =>
                OutcomeCategoryService.createOutcomeCategory(category)
              }
              program={program}
              buttonText={t("plo.addCategory")}
              type={"add"}
            />
          </Flex.Item>
          <Flex.Item>
            <PLOUploadModal program={program} />
          </Flex.Item>
        </Flex>
        <Flex wrap="wrap" gap="small">
          <Flex.Item size="100%">
            <ArchiveToggle
              label={t("learningOutcomes.archive.archivedPLOs")}
              uncategorizedLos={uncategorizedLos}
              categories={categories}
            />
          </Flex.Item>
          {unarchivedUncategorizedLos.length > 0 && (
            <Flex.Item size="100%">
              <ToggleGroup
                defaultExpanded
                toggleLabel={t("categories.uncategorized")}
                summary={t("categories.uncategorized")}
              >
                {unarchivedUncategorizedLos.map((outcome, key) => (
                  <Card
                    key={key}
                    style={{
                      color: "#0D323F",
                      backgroundColor: "#F2F8FA",
                      border: "1px solid #ccc",
                      borderRadius: 10,
                      padding: "5px 15px 15px 15px",
                      margin: 15,
                    }}
                  >
                    <PLOCard
                      outcome={outcome}
                      setLoadingPlos={setLoadingPlos}
                      programId={program.id}
                      handleArchiveOutcome={handleArchiveOutcome}
                      handleUpdateRatingScale={handleUpdateRatingScale}
                    />
                  </Card>
                ))}
              </ToggleGroup>
            </Flex.Item>
          )}
          {unarchivedCategories.map((category, key) => (
            <Flex.Item key={key} size="100%">
              <PLOCategories
                category={category}
                setLoadingPlos={setLoadingPlos}
                program={program}
                handleArchiveOutcome={handleArchiveOutcome}
                handleUpdateRatingScale={handleUpdateRatingScale}
              />
            </Flex.Item>
          ))}
        </Flex>
      </UncategorizedContext.Provider>
    </CategoriesContext.Provider>
  );
};

export { CategoriesContext, CategoryList, UncategorizedContext };

const AddOutcomeButton = ({ outcome, handleSave }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        id="add-outcome-button"
        color="primary"
        renderIcon={IconAddLine}
        onClick={() => setOpen(true)}
      >
        {t("plo.addButton")}
      </Button>
      <EditOutcomeModal
        open={open}
        setOpen={setOpen}
        outcome={outcome}
        handleSave={handleSave}
      />
    </>
  );
};
